import { useSnackbar } from "notistack";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ApiResponse } from "~/api/GeneratedApi";

export function useApiErrorHandler(): (err: ApiResponse | Error) => void {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const apiErrorHandler = useCallback(
    (err: ApiResponse | Error) => {
      if ("resultKey" in err) {
        if (err.modelErrors) {
          err.modelErrors?.forEach((key) =>
            key.forEach((e) => {
              const snackbarKey = enqueueSnackbar(
                t(`apiResponses:${e.errorMessage}`),
                {
                  variant: "warning",
                  preventDuplicate: true,
                  onClick: () => closeSnackbar(snackbarKey),
                },
              );
            }),
          );
        } else if (err.resultKey) {
          const key = enqueueSnackbar(t(`apiResponses:${err.resultKey}`), {
            variant: "warning",
            preventDuplicate: true,
            onClick: () => closeSnackbar(key),
          });
        }
      } else {
        const e = err as Error;
        const key = enqueueSnackbar(e.message, {
          variant: "warning",
          autoHideDuration: 10000,
          preventDuplicate: true,
          onClick: () => closeSnackbar(key),
        });
      }
    },
    [closeSnackbar, enqueueSnackbar, t],
  );

  return apiErrorHandler;
}

interface ApiErrorMessage {
  message: string;
}

interface ApiErrorKey {
  key: string;
}

export function apiErrorMessage(
  err: ApiResponse | Error,
): ApiErrorKey | ApiErrorMessage {
  if ("resultKey" in err && err.resultKey) {
    return { key: err.resultKey };
  }
  const e = err as Error;
  return { message: e.message };
}
